import { useMutation } from '@tanstack/react-query';
import type { AxiosError, AxiosInstance } from 'axios';
import { AmlFormSchema } from 'shared/components/InvoiceActionsDropdown/AmlModal/types/types';
import { z } from 'zod/v4';

import { type ApiResponse, addActionNameToMutationResult, useAxiosInstance } from './utils';

export const CreateAmlPdfSchema = AmlFormSchema.omit({ pep: true }).extend({
  pep: z.boolean(),
  idFront: z.instanceof(File).nullable(),
  idBack: z.instanceof(File).nullable(),
});

export type CreateAmlPdfData = z.infer<typeof CreateAmlPdfSchema>;

type CreateAmlPdfVariables = {
  data: CreateAmlPdfData;
};

export async function createAmlPdfFn(
  axiosInstance: Promise<AxiosInstance>,
  { data }: CreateAmlPdfVariables,
) {
  const instance = await axiosInstance;

  const formData = new FormData();

  const { idFront, idBack, directors, beneficialOwners, ...rest } = data;

  for (const [key, value] of Object.entries(rest)) formData.append(key, String(value));

  formData.append('directors', JSON.stringify(directors));
  formData.append('beneficial_owners', JSON.stringify(beneficialOwners));

  if (idFront) formData.append('id_front', idFront);
  if (idBack) formData.append('id_back', idBack);

  return instance.post('/aml_compliance_document', formData);
}

export function useCreateAmlPdf() {
  const axiosInstance = useAxiosInstance();

  const mutation = useMutation<
    ApiResponse,
    AxiosError<{ error_message: Array<string> }>,
    CreateAmlPdfVariables
  >({
    mutationFn: variables => createAmlPdfFn(axiosInstance, variables),
  });

  return addActionNameToMutationResult<
    ApiResponse,
    AxiosError<{ error_message: Array<string> }>,
    CreateAmlPdfVariables,
    'createAmlPdf'
  >('createAmlPdf', mutation);
}
